import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";

// Calls hidden from the practice in this session (ENG-2364), keyed by backend
// call id. Same reason as `call-attachments.slice.ts` and `call-tags.slice.ts`:
// the call lists accumulate rows in local `usePagination` state and are never
// invalidated (SN-18212), so a hide drops the row from every loaded list
// through this overlay and the next authoritative load confirms it. `false`
// is an unhide (Suno only): the row leaves the Hidden list the same way.
//
// `live` is the in-call "Private" toggle, keyed by the SDK call id because an
// outbound call the SDK placed has no backend id in the widget; the response
// supplies it. The entry lives for the call and goes with it.
export type LivePrivacyStatus = "pending" | "on" | "off" | "error";

export type LivePrivacy = {
  status: LivePrivacyStatus;
  backendCallId: number | null;
};

type HiddenCallsState = {
  byCallId: Record<number, boolean>;
  live: Record<string, LivePrivacy>;
};

const initialState: HiddenCallsState = { byCallId: {}, live: {} };

// The auth logout thunk's fulfilled action type; matched by string so this
// slice does not import the (import-heavy) auth slice.
const LOGOUT_FULFILLED = "auth/logout/fulfilled";

export const hiddenCallsSlice = createSlice({
  name: "hiddenCalls",
  initialState,
  reducers: {
    callHidden: (state, action: PayloadAction<{ callId: number }>) => {
      state.byCallId[action.payload.callId] = true;
    },
    callUnhidden: (state, action: PayloadAction<{ callId: number }>) => {
      state.byCallId[action.payload.callId] = false;
    },
    livePrivacyRequested: (
      state,
      action: PayloadAction<{ sdkCallId: string }>
    ) => {
      const current = state.live[action.payload.sdkCallId];
      state.live[action.payload.sdkCallId] = {
        status: "pending",
        backendCallId: current?.backendCallId ?? null
      };
    },
    livePrivacySet: (
      state,
      action: PayloadAction<{
        sdkCallId: string;
        backendCallId: number;
        on: boolean;
      }>
    ) => {
      const { sdkCallId, backendCallId, on } = action.payload;
      state.live[sdkCallId] = { status: on ? "on" : "off", backendCallId };
      state.byCallId[backendCallId] = on;
    },
    livePrivacyFailed: (
      state,
      action: PayloadAction<{ sdkCallId: string }>
    ) => {
      const current = state.live[action.payload.sdkCallId];
      state.live[action.payload.sdkCallId] = {
        status: "error",
        backendCallId: current?.backendCallId ?? null
      };
    },
    livePrivacyDropped: (
      state,
      action: PayloadAction<{ sdkCallId: string }>
    ) => {
      delete state.live[action.payload.sdkCallId];
    },
    hiddenCallsCleared: () => initialState
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === LOGOUT_FULFILLED,
      () => initialState
    );
  }
});

export const {
  callHidden,
  callUnhidden,
  livePrivacyRequested,
  livePrivacySet,
  livePrivacyFailed,
  livePrivacyDropped,
  hiddenCallsCleared
} = hiddenCallsSlice.actions;

export const selectHiddenCallsOverlay = (state: RootState) =>
  state.hiddenCalls.byCallId;

export const selectLivePrivacy = (
  state: RootState,
  sdkCallId: string | null
): LivePrivacy | undefined =>
  sdkCallId === null ? undefined : state.hiddenCalls.live[sdkCallId];

export const isHiddenCall = (
  call: { id: number; hidden_at?: string | null },
  overlay: Record<number, boolean>
) => overlay[call.id] ?? Boolean(call.hidden_at);

// Rows a list may keep. The ordinary feeds drop anything hidden; Suno's Hidden
// list (`showHidden`) keeps only what is still hidden.
export const withoutHiddenCalls = <
  T extends { id: number; hidden_at?: string | null }
>(
  rows: T[],
  overlay: Record<number, boolean>,
  showHidden = false
): T[] => {
  const touched = Object.keys(overlay).length > 0;
  if (!touched && !showHidden) {
    return rows.some((row) => Boolean(row.hidden_at))
      ? rows.filter((row) => !row.hidden_at)
      : rows;
  }
  return rows.filter((row) => isHiddenCall(row, overlay) === showHidden);
};

// Voicemails follow their call: one left on a hidden call leaves the mailbox.
export const withoutHiddenVoicemails = <T extends { call: number | null }>(
  rows: T[],
  overlay: Record<number, boolean>
): T[] =>
  Object.keys(overlay).length === 0
    ? rows
    : rows.filter((row) => row.call === null || overlay[row.call] !== true);

export default hiddenCallsSlice.reducer;
